/**
 * ComboboxFK — Searchable dropdown for FK fields with quick-add
 *
 * Supports FKOption.meta (extra info line) and FKOption.searchTags (full-text search).
 *
 * Usage:
 *   const { options, isLoading } = useCustomerLookup();
 *   <ComboboxFK
 *     value={field.state.value}
 *     onChange={field.handleChange}
 *     options={options}
 *     label="Khách hàng"
 *     quickAddLabel="Thêm KH mới"
 *     onQuickAdd={async (name) => { ... return newId; }}
 *   />
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, Plus, Search, Loader2, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn, normalizeVi, fuzzyMatchVi } from '@/lib/utils';
import type { FKOption } from '@/hooks/useFKLookup';
import { toast } from 'sonner';

interface ComboboxFKProps {
    value: string;
    valueLabel?: string;
    onChange: (id: string, option?: FKOption) => void;
    options: FKOption[];
    label?: string;
    placeholder?: string;
    required?: boolean;
    loading?: boolean;
    error?: boolean;
    disabled?: boolean;
    className?: string;
    id?: string;
    name?: string;
    dataField?: string;
    /** Quick-add button label */
    quickAddLabel?: string;
    /** Custom click handler for quick-add (e.g. to open a modal instead of a new tab) */
    onQuickAddClick?: (name: string) => void;
    /** Also set a name field when selecting */
    onNameChange?: (name: string) => void;
    /** Callback when a new item is created via quick add */
    onCreated?: (id: string) => void;
    /** Hide the external label (useful when rendered inside a table cell) */
    hideLabel?: boolean;
    /** Whether the user has permission to add new items. Defaults to true. */
    canAdd?: boolean;
    /** Form component to render inside a modal for Quick Add */
    addFormContent?: React.ReactNode;
    /** Title for the Quick Add modal */
    addFormTitle?: string;
    /** Size of the Quick Add modal. Defaults to 'md'. */
    addModalSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    /** External search change handler for backend filtering */
    onSearchChange?: (search: string) => void;
    /** Breadcrumb hierarchy for nested modal context, e.g. ['Sản phẩm', 'Vật tư'] */
    breadcrumb?: string[];
    /** Infinite scroll support */
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    onLoadMore?: () => void;
    /** Show entity subLabel (e.g. APV productCode F...) prominently inside selected trigger */
    showSelectedSubLabel?: boolean;
}

export function ComboboxFK({
    value,
    valueLabel,
    onChange,
    options,
    label,
    placeholder = 'Chọn...',
    required,
    loading,
    error,
    disabled,
    id,
    name,
    dataField,
    quickAddLabel,
    onQuickAddClick,
    onNameChange,
    onCreated,
    hideLabel,
    canAdd = true,
    addFormContent,
    addFormTitle,
    addModalSize = 'md',
    onSearchChange,
    breadcrumb,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    showSelectedSubLabel = false,
}: ComboboxFKProps) {
    const [open, setOpen] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [search, setSearch] = useState('');
    const [quickAddSearch, setQuickAddSearch] = useState('');
    const [adding, setAdding] = useState(false);
    const [displayLimit, setDisplayLimit] = useState(50);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [optimisticOption, setOptimisticOption] = useState<FKOption | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const preventCloseRef = useRef(false);
    const observerTarget = useRef<HTMLDivElement>(null);
    const triggerButtonRef = useRef<HTMLButtonElement>(null);

    // Automatically reconcile optimistic option once it appears in the server options list
    useEffect(() => {
        if (optimisticOption && options.some(o => o.id === optimisticOption.id)) {
            setOptimisticOption(null);
        }
    }, [options, optimisticOption]);

    // Effective options include optimisticOption immediately to prevent blank/placeholder flickers
    const effectiveOptions = useMemo(() => {
        if (!optimisticOption) return options;
        if (options.some(o => o.id === optimisticOption.id)) return options;
        return [optimisticOption, ...options];
    }, [options, optimisticOption]);

    // Infinite Scroll Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    onLoadMore?.();
                }
            },
            { threshold: 0.1 }
        );
        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, onLoadMore, open]);

    useEffect(() => {
        if (open) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
            setDisplayLimit(50);
        } else {
            setSearch('');
        }
    }, [open]);

    // Debounce search to parent
    useEffect(() => {
        if (!onSearchChange) return;
        const timer = setTimeout(() => {
            onSearchChange(search);
        }, 150);
        return () => clearTimeout(timer);
    }, [search, onSearchChange]);

    // Filter options by search
    const filtered = useMemo(() => {
        if (!search.trim()) return effectiveOptions;
        return effectiveOptions.filter(o => {
            if (fuzzyMatchVi(o.searchTags, search)) return true;
            if (fuzzyMatchVi(o.label, search)) return true;
            if (fuzzyMatchVi(o.subLabel, search)) return true;
            if (fuzzyMatchVi(o.meta, search)) return true;
            return false;
        });
    }, [effectiveOptions, search]);

    // Reset activeIndex to 0 when filtered options change
    useEffect(() => {
        setActiveIndex(0);
    }, [filtered]);

    // Auto-scroll to active index
    useEffect(() => {
        if (open && listRef.current && activeIndex >= 0) {
            const activeEl = listRef.current.children[activeIndex] as HTMLElement;
            if (activeEl && activeEl.scrollIntoView) {
                activeEl.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex, open]);

    // Find selected option - checked against effectiveOptions, optimisticOption, and valueLabel
    const selected = effectiveOptions?.find(o => o.id === value) ||
                     (optimisticOption && optimisticOption.id === value ? optimisticOption : null) ||
                     (value && valueLabel ? { id: value, label: valueLabel } : null);

    const handleSelect = (option: FKOption) => {
        onChange(option.id, option);
        if (onNameChange) onNameChange(option.label);
        setOpen(false);
    };

    const handleClear = (e: React.MouseEvent | React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onChange('');
        if (onNameChange) onNameChange('');
    };

    const handleShowMore = useCallback(() => {
        setDisplayLimit(prev => prev + 50);
    }, []);

    return (
        <div 
            className={cn("space-y-1.5", hideLabel && "space-y-0")}
            data-field={dataField || id || name}
        >
            {!hideLabel && label && (
                <Label className="text-xs font-medium text-slate-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
            )}
            <Popover open={open} onOpenChange={(v) => {
                if (!v && preventCloseRef.current) return;
                setOpen(v);
            }}>
                <PopoverTrigger asChild>
                    <Button
                        ref={triggerButtonRef}
                        id={id || name}
                        data-field={dataField || id || name}
                        variant="outline"
                        role="combobox"
                        aria-label={label || placeholder}
                        aria-expanded={open}
                        aria-invalid={error ? true : undefined}
                        disabled={disabled}
                        title={
                            selected
                                ? selected.subLabel && selected.subLabel !== selected.label
                                    ? `${selected.subLabel} — ${selected.label}`
                                    : selected.label
                                : valueLabel || undefined
                        }
                        className={cn(
                            'w-full justify-between h-9 text-sm font-normal',
                            !value && 'text-muted-foreground',
                            error && 'border-destructive bg-red-50/40 ring-2 ring-destructive/25 text-destructive font-bold',
                        )}
                    >
                        <span className="truncate flex-1 text-left">
                            {loading ? (
                                <span className="flex items-center gap-1.5 text-slate-400">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Đang tải...
                                </span>
                            ) : selected ? (
                                <span className="flex items-center gap-1.5 min-w-0">
                                    {selected.thumbnail && (
                                        <img src={selected.thumbnail} alt="" className="w-5 h-5 rounded object-cover shrink-0 border border-slate-200" />
                                    )}
                                    {showSelectedSubLabel &&
                                        selected.subLabel &&
                                        selected.subLabel !== selected.label &&
                                        !selected.label.includes(selected.subLabel) && (
                                            <span
                                                data-testid="combobox-selected-sublabel"
                                                className="font-mono text-[13px] font-bold tracking-tight text-indigo-800 bg-indigo-50 border border-indigo-300 px-1.5 py-0.5 rounded shrink-0 leading-none"
                                            >
                                                {selected.subLabel}
                                            </span>
                                        )}
                                    <span className="truncate">{selected.label}</span>
                                </span>
                            ) : value && valueLabel ? (
                                <span className="truncate">{valueLabel}</span>
                            ) : (
                                placeholder
                            )}
                        </span>
                        <div className="flex items-center gap-0.5 ml-1 shrink-0">
                            {value && !disabled && (
                                <div 
                                    className="p-1 -mr-1 hover:bg-slate-100 rounded-sm cursor-pointer pointer-events-auto relative z-10"
                                    onPointerDown={handleClear}
                                    onClick={handleClear}
                                >
                                    <X className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />
                                </div>
                            )}
                            <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
                        </div>
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
                    align="start"
                    onPointerDownOutside={(e) => { if (preventCloseRef.current) e.preventDefault(); }}
                    onFocusOutside={(e) => { if (preventCloseRef.current) e.preventDefault(); }}
                    onInteractOutside={(e) => { if (preventCloseRef.current) e.preventDefault(); }}
                >
                    {/* Search */}
                    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b">
                        <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <Input
                            ref={searchInputRef}
                            value={search}
                            onChange={e => {
                                setSearch(e.target.value);
                            }}
                            placeholder={`Tìm ${label ? label.toLowerCase() : '...'}...`}
                            className="h-7 text-xs border-0 shadow-none focus-visible:ring-0 px-0"
                            onKeyDown={e => {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setActiveIndex(prev => Math.min(prev + 1, filtered.length - 1));
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setActiveIndex(prev => Math.max(prev - 1, 0));
                                } else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (filtered.length > 0 && activeIndex >= 0 && activeIndex < filtered.length) {
                                        handleSelect(filtered[activeIndex]);
                                    } else if (filtered.length === 0 && canAdd && search.trim()) {
                                        if (onQuickAddClick) {
                                            onQuickAddClick(search.trim());
                                            setOpen(false);
                                        } else if (addFormContent) {
                                            setQuickAddSearch(search.trim());
                                            setShowAddModal(true);
                                            setOpen(false);
                                        }
                                    }
                                }
                            }}
                        />
                    </div>

                    {/* Options list */}
                    <div className="max-h-[280px] overflow-y-auto" ref={listRef}>
                        {filtered.length === 0 ? (
                            <div className="px-3 py-4 text-center text-xs text-slate-400">
                                Không tìm thấy kết quả
                            </div>
                        ) : (
                            <>
                                {filtered.slice(0, onLoadMore ? filtered.length : displayLimit).map((option, index) => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        role="option"
                                        aria-selected={index === activeIndex}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        className={cn(
                                            'w-full flex items-start gap-2 px-2 py-2 text-left text-xs transition-colors',
                                            index === activeIndex ? 'bg-slate-100' : 'hover:bg-slate-50',
                                            option.id === value && 'apv-accent-bg-soft'
                                        )}
                                        onPointerDown={(e) => { e.preventDefault(); }}
                                        onClick={() => handleSelect(option)}
                                    >
                                        <Check
                                            className={cn(
                                                'h-3.5 w-3.5 shrink-0 mt-0.5',
                                                option.id === value ? 'apv-accent-text' : 'invisible',
                                            )}
                                        />
                                        <div className="flex-1 min-w-0 flex items-start gap-2">
                                            {option.thumbnail && (
                                                <img src={option.thumbnail} alt="" className="w-6 h-6 rounded object-cover shrink-0 border border-slate-200 mt-px" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start gap-1.5 flex-wrap">
                                                    {option.subLabel && (
                                                        <span className="font-mono text-[10px] text-indigo-600 bg-indigo-50 px-1 rounded shrink-0 mt-px">
                                                            {option.subLabel}
                                                        </span>
                                                    )}
                                                    <span className="text-slate-900 font-medium leading-tight break-words">{option.label}</span>
                                                </div>
                                                {option.meta && (
                                                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight break-words">
                                                        {option.meta}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {!onLoadMore && filtered.length > displayLimit && (
                                    <button
                                        type="button"
                                        onClick={handleShowMore}
                                        className="w-full px-3 py-2 text-center text-[11px] text-blue-600 hover:bg-blue-50 border-t border-dashed transition-colors flex items-center justify-center gap-1"
                                    >
                                        <ChevronDown className="h-3 w-3" />
                                        Xem thêm ({filtered.length - displayLimit} còn lại)
                                    </button>
                                )}
                                {onLoadMore && (
                                    <div ref={observerTarget} className="py-2 flex items-center justify-center">
                                        {isFetchingNextPage ? (
                                            <div className="flex items-center gap-1 text-[11px] text-slate-500">
                                                <Loader2 className="h-3 w-3 animate-spin" /> Đang tải thêm...
                                            </div>
                                        ) : hasNextPage ? (
                                            <div className="h-4" /> 
                                        ) : null}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {canAdd && quickAddLabel && (addFormContent || onQuickAddClick) && (
                        <div className="border-t px-2 py-1.5">
                                <button
                                    type="button"
                                    disabled={adding}
                                    className={cn(
                                        'w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded transition-colors',
                                        'text-emerald-700 hover:bg-emerald-50 cursor-pointer',
                                    )}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        if (!onQuickAddClick) {
                                            preventCloseRef.current = true;
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onQuickAddClick) {
                                            onQuickAddClick(search.trim());
                                            setOpen(false);
                                        } else if (addFormContent) {
                                            setQuickAddSearch(search.trim());
                                            setShowAddModal(true);
                                            setOpen(false);
                                        }
                                    }}
                                >
                                    {adding ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Plus className="h-3.5 w-3.5" />
                                    )}
                                    <span>
                                        {adding ? 'Đang mở...' : (search.trim() ? `${quickAddLabel}: "${search}"` : quickAddLabel)}
                                    </span>
                                </button>
                        </div>
                    )}
                </PopoverContent>
            </Popover>

            {/* Quick Add Modal */}
            {addFormContent && (
                <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                    <DialogContent 
                        data-testid="combobox-fk-dialog-content"
                        // Form thêm nhanh: bấm ra ngoài / Esc không đóng, tránh mất dữ liệu đang nhập (FB-9CCX55)
                        onPointerDownOutside={(e) => e.preventDefault()}
                        onInteractOutside={(e) => e.preventDefault()}
                        onEscapeKeyDown={(e) => e.preventDefault()}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                            }
                        }}
                        onCloseAutoFocus={(e) => {
                            e.preventDefault();
                            requestAnimationFrame(() => {
                                triggerButtonRef.current?.focus();
                            });
                        }}
                        className={cn(
                            "max-w-[95vw] w-full max-h-[95vh] overflow-y-auto",
                            addModalSize === 'sm' && "sm:max-w-md",
                            addModalSize === 'md' && "sm:max-w-xl",
                            addModalSize === 'lg' && "sm:max-w-2xl lg:max-w-3xl",
                            addModalSize === 'xl' && "sm:max-w-4xl md:max-w-5xl lg:max-w-5xl",
                            addModalSize === 'full' && "sm:max-w-5xl lg:max-w-7xl"
                        )}
                    >
                        <DialogHeader className="sr-only">
                            <DialogTitle>{addFormTitle || "Thêm mới"}</DialogTitle>
                            <DialogDescription>Biểu mẫu tạo mới dữ liệu</DialogDescription>
                        </DialogHeader>
                        {React.isValidElement(addFormContent) 
                            ? React.cloneElement(addFormContent as React.ReactElement<any>, {
                                breadcrumb: [
                                    ...(breadcrumb || []),
                                    addFormTitle || quickAddLabel || "Thêm mới",
                                ],
                                onSuccess: (newItem: { id: string; label: string; subLabel?: string; original?: any }) => {
                                    const resolvedSubLabel =
                                        newItem.subLabel ||
                                        newItem.original?.productCode ||
                                        newItem.original?.code ||
                                        newItem.original?.materialId ||
                                        undefined;
                                    const opt: FKOption = {
                                        id: newItem.id,
                                        label: newItem.label,
                                        ...(resolvedSubLabel ? { subLabel: String(resolvedSubLabel) } : {}),
                                        original: newItem.original || newItem,
                                    };
                                    setOptimisticOption(opt);
                                    onChange(newItem.id, opt);
                                    if (onNameChange) onNameChange(newItem.label);
                                    if (onCreated) onCreated(newItem.id);
                                    setShowAddModal(false);
                                    setOpen(false);
                                    requestAnimationFrame(() => {
                                        triggerButtonRef.current?.focus();
                                    });
                                },
                                onCancel: () => {
                                    setShowAddModal(false);
                                    requestAnimationFrame(() => {
                                        triggerButtonRef.current?.focus();
                                    });
                                },
                                initialData: {
                                    ...(React.isValidElement(addFormContent) ? (addFormContent.props as any)?.initialData : {}),
                                    name: (React.isValidElement(addFormContent) && (addFormContent.props as any)?.initialData?.name) || quickAddSearch,
                                    productName: (React.isValidElement(addFormContent) && (addFormContent.props as any)?.initialData?.productName) || quickAddSearch,
                                }
                            }) 
                            : addFormContent
                        }
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
